/**
 * @fileoverview Upload state and validation for the registration documents section.
 * @module components/AppointmentRegistrationSections/Documents/useDocumentsSection
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  DOCUMENT_FIELD_KEYS,
  buildFieldLabels,
  buildFileRequirements,
  createInitialErrors,
  createInitialFiles,
  groupVisibleDocumentFields,
} from "./documentFieldConfig.js";

const TABLET_MAX_WIDTH = 1023;

/**
 * @param {Object} options
 * @param {Object} options.company
 * @param {Function} options.onFieldChange
 * @param {Function} options.onValidationChange
 */
export const useDocumentsSection = ({
  company,
  onFieldChange,
  onValidationChange,
}) => {
  const { t } = useTranslation();
  const prevHasErrorsRef = useRef(false);
  const onValidationChangeRef = useRef(onValidationChange);
  const isTablet = window.innerWidth <= TABLET_MAX_WIDTH;

  const [files, setFiles] = useState(() => createInitialFiles(company));
  const [errors, setErrors] = useState(createInitialErrors);
  const [isDragging, setIsDragging] = useState(false);
  const isUploading = false;

  const fileRequirements = useMemo(() => buildFileRequirements(t), [t]);
  const fieldLabels = useMemo(() => buildFieldLabels(t), [t]);

  const setFieldError = useCallback((fieldKey, message) => {
    setErrors((prev) => ({ ...prev, [fieldKey]: message }));
  }, []);

  const validateFile = useCallback(
    (file, fileType) => {
      const requirements = fileRequirements[fileType];
      if (!requirements) {
        return false;
      }
      if (!requirements.allowedTypes.includes(file.type)) {
        setFieldError(fileType, requirements.errorMessages.type);
        return false;
      }
      if (file.size > requirements.maxSize) {
        setFieldError(fileType, requirements.errorMessages.size);
        return false;
      }
      return true;
    },
    [fileRequirements, setFieldError],
  );

  const handleInputChange = useCallback(
    (field, value) => {
      if (onFieldChange) {
        onFieldChange(field, value);
      }
    },
    [onFieldChange],
  );

  const applyFileSelection = useCallback(
    (fileType, file) => {
      if (validateFile(file, fileType)) {
        setFiles((prev) => ({ ...prev, [fileType]: file }));
        setFieldError(fileType, "");
        handleInputChange(fileType, file);
        return;
      }
      setFiles((prev) => ({ ...prev, [fileType]: null }));
      handleInputChange(fileType, null);
    },
    [handleInputChange, setFieldError, validateFile],
  );

  const handleFileChange = useCallback(
    (event, fileType) => {
      const file = event.target.files[0];
      if (file) {
        applyFileSelection(fileType, file);
      }
    },
    [applyFileSelection],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event, fileType) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files[0];
      if (file) {
        applyFileSelection(fileType, file);
      }
    },
    [applyFileSelection],
  );

  const handleFileClick = useCallback((fileInputId) => {
    document.getElementById(fileInputId).click();
  }, []);

  const hasFileErrors = useCallback(
    () => DOCUMENT_FIELD_KEYS.some((key) => Boolean(errors[key])),
    [errors],
  );

  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  useEffect(() => {
    const currentHasErrors = hasFileErrors();
    if (
      onValidationChangeRef.current &&
      prevHasErrorsRef.current !== currentHasErrors
    ) {
      prevHasErrorsRef.current = currentHasErrors;
      onValidationChangeRef.current(currentHasErrors);
    }
  }, [errors, hasFileErrors]);

  return {
    t,
    isTablet,
    files,
    errors,
    isUploading,
    isDragging,
    fieldLabels,
    fieldsByRow: groupVisibleDocumentFields(company),
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileClick,
  };
};
